import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormTable, FormTableRow } from './FormTable'
import { RegisteredDatePicker } from '../../../components/fields/DatePicker'
import { ShouldShow } from '../../../components/entities/IssueType'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { Link } from 'react-router-dom'
import * as datefns from 'date-fns'
import { useWatch } from 'react-hook-form'

const getIncRightsMinDate = ({ publicationSourcePublishedDate, generalMeetingDate }) => {
    if (!publicationSourcePublishedDate && !generalMeetingDate) return
    const gmd = new Date(generalMeetingDate)
    const pspd = datefns.addDays(new Date(publicationSourcePublishedDate), 1)

    if (!publicationSourcePublishedDate) return gmd
    if (!generalMeetingDate) return pspd

    return datefns.max([gmd, pspd])
}

const validateDates = (formValues) => {
    let isValid = true

    if (
        formValues.generalMeetingDate &&
        formValues.publicationDate &&
        formValues.incRightsDate &&
        datefns.isBefore(
            new Date(formValues.incRightsDate),
            getIncRightsMinDate({
                publicationSourcePublishedDate: formValues.publicationDate,
                generalMeetingDate: formValues.generalMeetingDate,
            }),
        )
    ) {
        isValid = false
    }
    if (
        formValues.incRightsDate &&
        formValues.exRightsDate &&
        datefns.isBefore(new Date(formValues.exRightsDate), datefns.addDays(new Date(formValues.incRightsDate), 1))
    ) {
        isValid = false
    }
    if (
        formValues.exRightsDate &&
        formValues.recordDate &&
        datefns.isBefore(new Date(formValues.recordDate), datefns.addDays(new Date(formValues.exRightsDate), 1))
    ) {
        isValid = false
    }
    if (
        formValues.recordDate &&
        formValues.rightsStartDate &&
        datefns.isBefore(new Date(formValues.rightsStartDate), new Date(formValues.recordDate))
    ) {
        isValid = false
    }
    if (
        formValues.recordDate &&
        formValues.subStartDate &&
        datefns.isBefore(new Date(formValues.subStartDate), datefns.addDays(new Date(formValues.recordDate), 1))
    ) {
        isValid = false
    }
    if (
        formValues.rightsStartDate &&
        formValues.rightsEndDate &&
        datefns.isBefore(
            new Date(formValues.rightsEndDate),
            datefns.addDays(datefns.max([new Date(formValues.subStartDate), new Date(formValues.rightsStartDate)]), 1),
        )
    ) {
        isValid = false
    }
    if (
        formValues.rightsEndDate &&
        formValues.subEndDate &&
        datefns.isBefore(
            new Date(formValues.subEndDate),
            datefns.addDays(datefns.max([new Date(formValues.subStartDate), new Date(formValues.rightsEndDate)]), 1),
        )
    ) {
        isValid = false
    }

    return isValid
}

function ImportantDates({ issue, publicationSource, voteDisableSave }) {
    const { t } = useTranslation()
    const formValues = useWatch()

    const [validDates, setValidDates] = React.useState(validateDates(formValues))

    // const [isSaveDisabled, setIsSaveDisabled] = React.useState(true)
    const [error, setError] = React.useState({})
    const i18nPrefix = 'entities.issue.fields.'
    React.useEffect(() => {
        if (voteDisableSave) {
            const valid = validateDates(formValues)
            if (valid !== validDates) {
                console.log(`valid: ${valid}, validDates: ${validDates}`)
                setValidDates(valid)
                voteDisableSave('dates', !valid)
            }
        }
    }, [formValues, validDates, voteDisableSave])

    let publicationDate = <>{issue.publicationDate ? formatDateYYYYMMDD(issue.publicationDate) : 'Saknas'}</>
    if (publicationSource) {
        publicationDate = (
            <>
                {formatDateYYYYMMDD(publicationSource.publishedDate)} {': '}
                <Link to={publicationSource.sourceUrl} target='_blank'>
                    {publicationSource.title}
                </Link>
            </>
        )
    }

    const handleError = (field, newError) => {
        console.log(`Error in ${field}: ${newError}`)
        setError({ ...error, [field]: newError })
    }

    return (
        <FormTable>
            <ShouldShow issueType={issue.type} issueField='publicationDate'>
                <FormTableRow label='Publiceringsdatum'>{publicationDate}</FormTableRow>
            </ShouldShow>

            <ShouldShow key={'generalMeetingDate'} issueType={issue.type} issueField={'generalMeetingDate'}>
                <FormTableRow key={'generalMeetingDate'} label={t(`${i18nPrefix}generalMeetingDate`)}>
                    <RegisteredDatePicker
                        name={'generalMeetingDate'}
                        i18nLabel={`${i18nPrefix}generalMeetingDate`}
                        minDate={datefns.addDays(new Date(publicationSource?.publishedDate), 1)}
                        referenceDate={
                            publicationSource?.publishedDate
                                ? datefns.addDays(new Date(publicationSource?.publishedDate), 1)
                                : new Date()
                        }
                        onError={(newError) => handleError('generalMeetingDate', newError)}
                    />
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'incRightsDate'} issueType={issue.type} issueField={'incRightsDate'}>
                <FormTableRow key={'incRightsDate'} label={t(`${i18nPrefix}incRightsDate`)}>
                    <RegisteredDatePicker
                        name={'incRightsDate'}
                        i18nLabel={`${i18nPrefix}incRightsDate`}
                        minDate={getIncRightsMinDate({
                            publicationSourcePublishedDate: publicationSource?.publishedDate,
                            generalMeetingDate: formValues.generalMeetingDate,
                        })}
                        referenceDate={
                            formValues.generalMeetingDate || publicationSource?.publishedDate
                                ? getIncRightsMinDate({
                                      publicationSourcePublishedDate: publicationSource?.publishedDate,
                                      generalMeetingDate: formValues.generalMeetingDate,
                                  })
                                : new Date()
                        }
                        onError={(newError) => handleError('incRightsDate', newError)}
                    />
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'exRightsDate'} issueType={issue.type} issueField={'exRightsDate'}>
                <FormTableRow key={'exRightsDate'} label={t(`${i18nPrefix}exRightsDate`)}>
                    <RegisteredDatePicker
                        name={'exRightsDate'}
                        i18nLabel={`${i18nPrefix}exRightsDate`}
                        minDate={datefns.addDays(new Date(formValues.incRightsDate), 1)}
                        referenceDate={
                            formValues.incRightsDate
                                ? datefns.addDays(new Date(formValues.incRightsDate), 1)
                                : new Date()
                        }
                        onError={(newError) => handleError('exRightsDate', newError)}
                    />
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'recordDate'} issueType={issue.type} issueField={'recordDate'}>
                <FormTableRow key={'recordDate'} label={t(`${i18nPrefix}recordDate`)}>
                    <RegisteredDatePicker
                        name={'recordDate'}
                        i18nLabel={`${i18nPrefix}recordDate`}
                        minDate={datefns.addDays(new Date(formValues.exRightsDate), 1)}
                        referenceDate={
                            formValues.exRightsDate ? datefns.addDays(new Date(formValues.exRightsDate), 1) : new Date()
                        }
                        onError={(newError) => handleError('recordDate', newError)}
                    />
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'rightsStartDate'} issueType={issue.type} issueField={'rightsStartDate'}>
                <FormTableRow key={'rightsStartDate'} label={t(`${i18nPrefix}rightsStartDate`)}>
                    <RegisteredDatePicker
                        name={'rightsStartDate'}
                        i18nLabel={`${i18nPrefix}rightsStartDate`}
                        minDate={new Date(formValues.recordDate)}
                        referenceDate={formValues.recordDate ? new Date(formValues.recordDate) : new Date()}
                        onError={(newError) => handleError('rightsStartDate', newError)}
                    />
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'subStartDate'} issueType={issue.type} issueField={'subStartDate'}>
                <FormTableRow key={'subStartDate'} label={t(`${i18nPrefix}subStartDate`)}>
                    <RegisteredDatePicker
                        name={'subStartDate'}
                        i18nLabel={`${i18nPrefix}subStartDate`}
                        minDate={datefns.addDays(new Date(formValues.recordDate), 1)}
                        referenceDate={
                            formValues.recordDate ? datefns.addDays(new Date(formValues.recordDate), 1) : new Date()
                        }
                        onError={(newError) => handleError('subStartDate', newError)}
                    />
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'rightsEndDate'} issueType={issue.type} issueField={'rightsEndDate'}>
                <FormTableRow key={'rightsEndDate'} label={t(`${i18nPrefix}rightsEndDate`)}>
                    <RegisteredDatePicker
                        name={'rightsEndDate'}
                        i18nLabel={`${i18nPrefix}rightsEndDate`}
                        minDate={datefns.addDays(
                            datefns.max([new Date(formValues.subStartDate), new Date(formValues.rightsStartDate)]),
                            1,
                        )}
                        referenceDate={
                            formValues.subStartDate || formValues.rightsStartDate
                                ? datefns.addDays(
                                      datefns.max([
                                          new Date(formValues.subStartDate),
                                          new Date(formValues.rightsStartDate),
                                      ]),
                                      1,
                                  )
                                : new Date()
                        }
                        onError={(newError) => handleError('rightsEndDate', newError)}
                    />
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'subEndDate'} issueType={issue.type} issueField={'subEndDate'}>
                <FormTableRow key={'subEndDate'} label={t(`${i18nPrefix}subEndDate`)}>
                    <RegisteredDatePicker
                        name={'subEndDate'}
                        i18nLabel={`${i18nPrefix}subEndDate`}
                        minDate={datefns.addDays(
                            datefns.max([new Date(formValues.subStartDate), new Date(formValues.rightsEndDate)]),
                            1,
                        )}
                        referenceDate={
                            formValues.subStartDate || formValues.rightsEndDate
                                ? datefns.addDays(
                                      datefns.max([
                                          new Date(formValues.subStartDate),
                                          new Date(formValues.rightsEndDate),
                                      ]),
                                      1,
                                  )
                                : new Date()
                        }
                        onError={(newError) => handleError('subEndDate', newError)}
                    />
                </FormTableRow>
            </ShouldShow>
            <ShouldShow key={'ipoTradingDate'} issueType={issue.type} issueField={'ipoTradingDate'}>
                <FormTableRow key={'ipoTradingDate'} label={t(`${i18nPrefix}ipoTradingDate`)}>
                    <RegisteredDatePicker
                        name={'ipoTradingDate'}
                        i18nLabel={`${i18nPrefix}ipoTradingDate`}
                        minDate={datefns.addDays(
                            datefns.max([new Date(formValues.subStartDate), new Date(formValues.rightsEndDate)]),
                            1,
                        )}
                        referenceDate={
                            formValues.subStartDate || formValues.rightsEndDate
                                ? datefns.addDays(
                                      datefns.max([
                                          new Date(formValues.subStartDate),
                                          new Date(formValues.rightsEndDate),
                                      ]),
                                      1,
                                  )
                                : new Date()
                        }
                        onError={(newError) => handleError('ipoTradingDate', newError)}
                    />
                </FormTableRow>
            </ShouldShow>
        </FormTable>
    )
}

export default ImportantDates
